import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserIcon,
  UsersIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ChartBarIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import agencyService from '../../services/agencyService';
import { useToast } from '../../contexts/ToastContext';

const AgencyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  
  const [agency, setAgency] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAgencyData();
  }, [id]);

  const fetchAgencyData = async () => {
    try {
      setLoading(true);
      const [agencyData, stats] = await Promise.all([
        agencyService.getAgency(id),
        agencyService.getAgencyStatistics(id)
      ]);
      
      setAgency(agencyData);
      setStatistics(stats);
    } catch (error) {
      showError('Erreur lors du chargement des données de l\'agence');
    } finally {
      setLoading(false);
    }
  };

  const performanceData = [
    { month: 'Jan', clients: 45, loans: 38 },
    { month: 'Fév', clients: 52, loans: 42 },
    { month: 'Mar', clients: 48, loans: 45 },
    { month: 'Avr', clients: 55, loans: 50 },
    { month: 'Mai', clients: 58, loans: 52 },
    { month: 'Juin', clients: 62, loans: 58 },
  ];

  const loanStatusData = [
    { name: 'Actifs', value: statistics.active_loans || 0 },
    { name: 'En attente', value: 5 },
    { name: 'Terminés', value: 15 },
    { name: 'Défaillants', value: 2 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement de l'agence...</p>
        </div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="text-center py-12">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Agence non trouvée</h3>
        <p className="mt-1 text-sm text-gray-500">
          L'agence que vous recherchez n'existe pas.
        </p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/agencies')}
            className="btn-primary"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color = 'blue', trend }) => {
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      purple: 'bg-purple-100 text-purple-600',
    };

    return (
      <div className="card">
        <div className="p-6">
          <div className="flex items-center">
            <div className={`flex-shrink-0 rounded-md p-3 ${colorClasses[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {typeof value === 'number' 
                  ? value.toLocaleString('fr-FR') 
                  : value}
              </p>
              {trend && (
                <div className="flex items-center mt-1">
                  <span className={`text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}%
                  </span>
                  <span className="text-xs text-gray-500 ml-2">vs mois dernier</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/agencies')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Retour aux agences
          </button>
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-lg bg-blue-100 flex items-center justify-center">
              <BuildingOfficeIcon className="h-10 w-10 text-blue-600" />
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-semibold text-gray-900">
                {agency.name}
              </h1>
              <div className="flex items-center mt-1">
                <p className="text-sm text-gray-500 mr-4">
                  Code: {agency.code}
                </p>
                <span className="badge badge-success">
                  Active
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/agencies/edit/${agency.id}`)}
            className="btn-secondary"
          >
            Modifier
          </button>
          <button
            onClick={() => navigate(`/clients?agency=${agency.id}`)}
            className="btn-primary"
          >
            Voir les clients
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'performance', 'users', 'reports'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab === 'overview' && 'Aperçu'}
              {tab === 'performance' && 'Performance'}
              {tab === 'users' && 'Utilisateurs'}
              {tab === 'reports' && 'Rapports'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Total Clients"
                value={statistics.total_clients || 0}
                icon={UsersIcon}
                color="blue"
                trend={12.5}
              />
              <StatCard
                title="Épargne Totale"
                value={`${(statistics.total_savings || 0).toLocaleString('fr-FR')} FCFA`}
                icon={CreditCardIcon}
                color="green"
                trend={8.3}
              />
              <StatCard
                title="Prêts Actifs"
                value={statistics.active_loans || 0}
                icon={CurrencyDollarIcon}
                color="yellow"
                trend={15.2}
              />
              <StatCard
                title="Performance"
                value="92.5%"
                icon={ChartBarIcon}
                color="purple"
                trend={2.1}
              />
            </div>

            {/* Agency Details */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Informations de l'agence
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Adresse</p>
                        <p className="text-sm font-medium text-gray-900">
                          {agency.address || 'Non renseignée'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Téléphone</p>
                        <p className="text-sm font-medium text-gray-900">
                          {agency.phone || 'Non renseigné'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-sm font-medium text-gray-900">
                          {agency.email || 'Non renseigné'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Responsable</p>
                        <p className="text-sm font-medium text-gray-900">
                          {agency.manager_name || 'Non assigné'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Date de création</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(agency.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Chart */}
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Performance des prêts
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={loanStatusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {loanStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value} prêts`, 'Nombre']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Activité récente
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[
                        { date: '2024-01-15', type: 'Nouveau prêt', description: 'Prêt agricole', amount: '500,000 FCFA' },
                        { date: '2024-01-14', type: 'Dépôt', description: 'Épargne client', amount: '150,000 FCFA' },
                        { date: '2024-01-13', type: 'Remboursement', description: 'Prêt commerce', amount: '75,000 FCFA' },
                        { date: '2024-01-12', type: 'Nouveau client', description: 'Inscription', amount: '-' },
                      ].map((activity, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(activity.date).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                              ${activity.type === 'Nouveau prêt' ? 'bg-blue-100 text-blue-800' : ''}
                              ${activity.type === 'Dépôt' ? 'bg-green-100 text-green-800' : ''}
                              ${activity.type === 'Remboursement' ? 'bg-yellow-100 text-yellow-800' : ''}
                              ${activity.type === 'Nouveau client' ? 'bg-purple-100 text-purple-800' : ''}
                            `}>
                              {activity.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {activity.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {activity.amount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-6">
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Évolution des indicateurs
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                     {/*<Legend />*/} 
                      <Bar dataKey="clients" name="Nouveaux clients" fill="#8884d8" />
                      <Bar dataKey="loans" name="Nouveaux prêts" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Indicateurs clés
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Taux de recouvrement</span>
                      <span className="text-sm font-medium text-gray-900">92.5%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Taux de défaillance</span>
                      <span className="text-sm font-medium text-gray-900">3.2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Satisfaction clients</span>
                      <span className="text-sm font-medium text-gray-900">4.5/5</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Croissance mensuelle</span>
                      <span className="text-sm font-medium text-green-600">+12.3%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Objectifs mensuels
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Nouveaux clients</span>
                        <span className="text-sm text-gray-500">45/50</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Volume des prêts</span>
                        <span className="text-sm text-gray-500">8.2M/10M FCFA</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: '82%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Recouvrement</span>
                        <span className="text-sm text-gray-500">92.5%/95%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '97.4%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Rapports disponibles
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  { title: 'Rapport financier', description: 'Revenus et dépenses', icon: '💰' },
                  { title: 'Portefeuille prêts', description: 'Statut des prêts', icon: '📊' },
                  { title: 'Clients', description: 'Démographie et activité', icon: '👥' },
                  { title: 'Performance', description: 'Indicateurs clés', icon: '📈' },
                  { title: 'Risques', description: 'Analyse des risques', icon: '⚠️' },
                  { title: 'Compliance', description: 'Conformité réglementaire', icon: '📋' },
                ].map((report, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6 hover:border-primary-300 hover:shadow-md transition-all duration-300">
                    <div className="flex items-center">
                      <span className="text-2xl mr-4">{report.icon}</span>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{report.title}</h4>
                        <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                      <button className="text-xs text-primary-600 hover:text-primary-900">
                        Voir
                      </button>
                      <button className="text-xs text-gray-600 hover:text-gray-900">
                        Exporter
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgencyDetail;